import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from './api';
import type { AdminFlowModel } from './types';
import FlowModelEditor from './components/FlowModelEditor';
import FlowModelList from './components/FlowModelList';

// ── Utilities ─────────────────────────────────────────────────────────────────

const emptyModel = (): AdminFlowModel => ({
  id: crypto.randomUUID(), title: '', desc: '', category: '', image: '', hiddenImagePrompt: '', questions: [],
});

// ── FlowModelManagement ───────────────────────────────────────────────────────

type View = { type: 'list' } | { type: 'editor'; mode: 'create' | 'edit'; initial: AdminFlowModel };

const FlowModelManagement: React.FC<{ onSectionDataChange?: (data: Record<string, any>) => void }> = ({ onSectionDataChange }) => {
  const [models, setModels] = useState<AdminFlowModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>({ type: 'list' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getFlowModels();
      setModels(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 回報列表資料給 AI（僅列表視圖時）
  useEffect(() => {
    if (view.type === 'list') {
      onSectionDataChange?.({
        view: 'list',
        models: models.map(m => ({ id: m.id, title: m.title, category: m.category, questionsCount: m.questions.length })),
      });
    }
  }, [models, view.type, onSectionDataChange]);

  const handleDelete = async (id: string) => {
    if (!confirm(`確定要刪除 "${id}"？`)) return;
    try {
      await adminApi.deleteFlowModel(id);
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDone = () => {
    setView({ type: 'list' });
    load();
  };

  if (view.type === 'editor') {
    return <FlowModelEditor mode={view.mode} initial={view.initial} onDone={handleDone} onSectionDataChange={onSectionDataChange} />;
  }

  return (
    <FlowModelList
      models={models}
      loading={loading}
      error={error}
      onRefresh={load}
      onCreate={() => setView({ type: 'editor', mode: 'create', initial: emptyModel() })}
      onEdit={item => setView({ type: 'editor', mode: 'edit', initial: item })}
      onDelete={handleDelete}
      onImport={model => {
        const isExisting = models.some(m => m.id === model.id);
        setView({ type: 'editor', mode: isExisting ? 'edit' : 'create', initial: model });
      }}
      onReorder={(from, to) => {
        setModels(prev => {
          const next = [...prev];
          const [removed] = next.splice(from, 1);
          next.splice(to, 0, removed);
          adminApi.reorderFlowModels(next.map(m => m.id)).catch(() => {});
          return next;
        });
      }}
    />
  );
};

export default FlowModelManagement;
